"use client";

import { SelectOffices } from "@cmp-src/form/OfficeSelectorInput/SelectOffices";
import { Division, IResource } from "@gettactic/api";
import { useFloors } from "@gettactic/hooks";
import { MapEditorUIContext } from "@gettactic/map-builder/src/components/map/contexts/MapEditorContext/useMapEditorUI";
import { EditorSelect } from "@gettactic/map-builder/src/components/ui/EditorSelect";
import React from "react";

export interface MapBuilderFloorSwitcherProps {
  /** Current office (floor-editing mode). */
  officeId?: string;
  /** Current floor (floor-editing mode). */
  floorId?: string;
  /** Current division (campus-editing mode). */
  divisionId?: string;
  onSwitchFloor?: (officeId: string, floor: IResource) => void;
  /** When provided, campus divisions with a map are switch targets too. */
  onSwitchDivision?: (division: Division) => void;
  /** Dirty flag when rendered outside the Konva editor's providers (the
   *  SVG editor passes it in); otherwise read from the editor context. */
  isDirty?: boolean;
}

/**
 * Office/floor/campus switchers rendered in the map builder's top-left
 * (via the headerSlot), so users can move between any map without leaving
 * the editor. The office picker is the same divisions-grouped selector
 * the map page uses — campuses with maps appear as selectable entries.
 * Rendered inside the editor providers, the unsaved-changes guard reads
 * the editor's dirty flag directly.
 */
export const MapBuilderFloorSwitcher: React.FC<
  MapBuilderFloorSwitcherProps
> = ({
  officeId,
  floorId,
  divisionId,
  onSwitchFloor,
  onSwitchDivision,
  isDirty: isDirtyProp
}) => {
  const editorUi = React.useContext(MapEditorUIContext);
  const isDirty = isDirtyProp ?? editorUi?.isDirty ?? false;

  // Office picked in the dropdown — floors reload for it, but the editor
  // only switches once a floor is chosen.
  const [pendingOfficeId, setPendingOfficeId] = React.useState(officeId ?? "");
  const { floors } = useFloors(onSwitchFloor ? pendingOfficeId || null : null);

  const confirmDiscard = () =>
    !isDirty ||
    window.confirm("Switch maps? Unsaved changes on this map will be lost.");

  const handleFloorChange = (nextFloorId: string) => {
    if (!onSwitchFloor) return;
    if (nextFloorId === floorId && pendingOfficeId === officeId) return;
    const floor = (floors || []).find((f) => f.id === nextFloorId);
    if (!floor) return;
    if (!confirmDiscard()) return;
    onSwitchFloor(pendingOfficeId, floor);
  };

  const handleDivisionSelect = (division: Division) => {
    if (!onSwitchDivision) return;
    if (division.id === divisionId) return;
    if (!confirmDiscard()) return;
    onSwitchDivision(division);
  };

  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <SelectOffices
        offices={pendingOfficeId ? [pendingOfficeId] : []}
        onChange={(ids) => {
          const nextOfficeId = ids[0];
          if (nextOfficeId) setPendingOfficeId(nextOfficeId);
        }}
        onSelectDivisionMap={
          onSwitchDivision ? handleDivisionSelect : undefined
        }
        selectedDivisionMapId={divisionId ?? null}
        variant="ghost"
        placeholder="Select office"
        triggerClassName="h-8 text-sm"
      />
      {/* No floor segment while a campus is being edited — it appears once
          an office is picked, as the completion step of switching back. */}
      {onSwitchFloor && pendingOfficeId ? (
        <>
          <span className="flex-none text-neutral-400 text-sm">/</span>
          <div className="w-36">
            <EditorSelect
              label="Floor"
              value={pendingOfficeId === officeId ? floorId || "" : ""}
              onValueChange={handleFloorChange}
              options={(floors || []).map((floor) => ({
                value: floor.id,
                label: floor.name || floor.id
              }))}
            />
          </div>
        </>
      ) : null}
    </div>
  );
};
